import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { MapPin, Footprints, Beer, Coffee, UtensilsCrossed, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  { key: "all", label: "All" },
  { key: "bar", label: "Bars & pubs", icon: Beer },
  { key: "cafe", label: "Cafés", icon: Coffee },
  { key: "restaurant", label: "Restaurants", icon: UtensilsCrossed },
];
const ICON = { bar: Beer, cafe: Coffee, restaurant: UtensilsCrossed };

function SpotRow({ s, onPropose, busy }) {
  const Icon = ICON[s.group] || MapPin;
  return (
    <div data-testid={`meetup-spot-${s.id}`} className="glass rounded-2xl p-3.5 flex items-center gap-3">
      <div className="h-10 w-10 rounded-full bg-amber-400/10 text-amber-300 flex items-center justify-center shrink-0"><Icon className="h-4.5 w-4.5" /></div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{s.name}</p>
        <p className="text-[11px] text-emerald-100/60 flex items-center gap-1.5">
          {s.label} · <Footprints className="h-3 w-3" /> {s.walk_min} min walk{s.address ? ` · ${s.address}` : ""}
        </p>
        <a href={s.map_url} target="_blank" rel="noopener noreferrer" data-testid={`meetup-map-${s.id}`} className="text-[11px] text-sky-300 hover:text-sky-200">Open map</a>
      </div>
      <button data-testid={`meetup-propose-${s.id}`} disabled={busy} onClick={() => onPropose(s)}
        className="btn-gold rounded-full h-9 px-4 text-xs font-semibold disabled:opacity-50 shrink-0">Propose</button>
    </div>
  );
}

export function MeetupIdeasButton({ matchId, venue, onProposed }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [group, setGroup] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setOpen(true);
    if (data?.spots?.length) return;
    setData(null);
    try { setData((await api.get(`/matches/${matchId}/meetup-spots`)).data); }
    catch (e) { setData({ spots: [], error: true }); toast.error(apiError(e.response?.data?.detail) || "Couldn't load spots"); }
  };

  const propose = async (s) => {
    setBusy(true);
    try { onProposed((await api.post(`/matches/${matchId}/meetup`, { spot_id: s.id })).data); setOpen(false); toast.success(`Suggested ${s.name}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't send suggestion"); }
    setBusy(false);
  };

  const spots = (data?.spots || []).filter((s) => group === "all" || s.group === group);
  return (
    <>
      <button data-testid="meetup-ideas-btn" onClick={load}
        className="w-full mb-2 h-10 rounded-full text-sm font-semibold flex items-center justify-center gap-2 border border-amber-400/40 text-amber-100 hover:bg-amber-400/10 transition-colors">
        <MapPin className="h-4 w-4" /> Meet-up ideas near {venue || "the venue"}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" data-testid="meetup-sheet" className="glass border-amber-500/20 max-h-[85vh] overflow-y-auto rounded-t-3xl">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display">Meet before the event</SheetTitle>
            <SheetDescription className="text-emerald-100/60">Spots within a 10-minute walk of {venue}. Propose one and your match can say yes.</SheetDescription>
          </SheetHeader>
          {data?.chosen && <p data-testid="meetup-chosen" className="mt-3 text-xs text-emerald-300">Agreed spot: <b>{data.chosen.name}</b></p>}
          <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar">
            {GROUPS.map((g) => (
              <button key={g.key} data-testid={`meetup-filter-${g.key}`} onClick={() => setGroup(g.key)}
                className={`px-3.5 h-8 rounded-full text-xs border whitespace-nowrap transition-colors ${group === g.key ? "border-amber-400/60 bg-amber-400/10 text-amber-200" : "border-white/10 text-white/70"}`}>{g.label}</button>
            ))}
          </div>
          <div className="space-y-2.5 mt-4 pb-4">
            {!data && <div data-testid="meetup-loading" className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-300" /></div>}
            {data && spots.length === 0 && (
              <p data-testid="meetup-empty" className="text-sm text-emerald-100/60 text-center py-8">
                {data.error ? "Couldn't reach the map right now — try again in a minute." : "No spots found here yet."}
              </p>
            )}
            {spots.map((s) => <SpotRow key={s.id} s={s} onPropose={propose} busy={busy} />)}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

const STATUS = { accepted: "It's a plan ✓", declined: "Maybe somewhere else", replaced: "Replaced by a newer idea" };

export function MeetupCard({ m, mine, matchId, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const { spot, status } = m.meetup;
  const respond = async (accept) => {
    setBusy(true);
    try { onUpdate(m.id, (await api.post(`/matches/${matchId}/meetup/${m.id}/respond`, { accept })).data.meetup); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't respond"); }
    setBusy(false);
  };
  return (
    <div data-testid="meetup-card" className={`max-w-[80%] rounded-[18px] p-3.5 border ${mine ? "border-amber-400/40 bg-amber-400/10 rounded-br-md" : "border-sky-400/30 bg-sky-400/5 rounded-bl-md"}`}>
      <p className="text-[10px] uppercase tracking-wide text-amber-300/80 flex items-center gap-1"><MapPin className="h-3 w-3" /> Meet-up idea</p>
      <p className="font-display font-bold mt-1">{spot.name}</p>
      <p className="text-[11px] text-emerald-100/60">{spot.label} · {spot.walk_min} min walk to the venue{spot.address ? ` · ${spot.address}` : ""}</p>
      <a href={spot.map_url} target="_blank" rel="noopener noreferrer" data-testid="meetup-card-map" className="text-[11px] text-sky-300 hover:text-sky-200">Open map</a>
      {status === "proposed" && !mine ? (
        <div className="flex gap-2 mt-2.5">
          <button data-testid="meetup-accept" disabled={busy} onClick={() => respond(true)} className="btn-gold rounded-full h-8 px-3.5 text-xs font-semibold flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Sounds good</button>
          <button data-testid="meetup-decline" disabled={busy} onClick={() => respond(false)} className="rounded-full h-8 px-3.5 text-xs border border-white/15 text-white/70 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Somewhere else</button>
        </div>
      ) : (
        <p data-testid="meetup-status" className={`text-xs mt-2 font-semibold ${status === "accepted" ? "text-emerald-300" : "text-white/50"}`}>
          {status === "proposed" ? "Waiting for a reply…" : STATUS[status]}
        </p>
      )}
    </div>
  );
}
